<template>
  <dialog ref="dialog" class="mx-auto my-auto shadow-lg" data-cy="cmd-k-modal">
    <slot name="header">
      <div class="p-4">
        <search-input
          no-padding
          no-margin
          ai
          tab-index="0"
          :value="aiSearchPhrase"
          @keyup="updateAiSearchPhrase"
        />
      </div>
    </slot>
    <slot v-if="showContent" name="content">
      <ai-deal-search-results @modal:close="closeModal" />
    </slot>
  </dialog>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, watch } from "vue";
import { useAiSearchStore } from "@/stores/aiSearch";
import SearchInput from "@/components/ui/inputs/SearchInput.vue";
import AiDealSearchResults from "@/components/cmdK/AiDealSearchResults.vue";
import debounce from "lodash/debounce";

const emit = defineEmits<{
  close: [];
}>();

const { open = false } = defineProps<{ open?: boolean }>();

const store = useAiSearchStore();

const dialog = ref<HTMLDialogElement>();
const aiSearchPhrase = ref<string>("");
const showContent = ref(false);

const closeModal = () => emit("close");

const updateAiSearchPhrase = debounce((event: KeyboardEvent) => {
  const { value } = event.target as HTMLInputElement;
  aiSearchPhrase.value = value.trim();
}, 600);

// using showModal allows use of 'esc' key to close the modal natively and gives a dialog backdrop we can style
onMounted(() => {
  dialog.value?.addEventListener("cancel", closeModal);
  if (open) {
    dialog.value?.showModal();
  }
});

onBeforeUnmount(() => {
  dialog.value?.removeEventListener("cancel", closeModal);
});

watch(
  () => open,
  (newValue) => (newValue ? dialog.value?.showModal() : dialog.value?.close())
);

watch(aiSearchPhrase, async (newValue, oldValue) => {
  if (newValue === oldValue || newValue === "") {
    return;
  }
  showContent.value = true;
  await store.aiSearchDeals(aiSearchPhrase.value);
});
</script>

<style scoped>
@reference "@/assets/tailwind.css";

dialog {
  @apply w-[560px] rounded-md bg-white;
  &:focus-within {
    @apply outline-none;
  }
  &::backdrop {
    backdrop-filter: blur(0.5px);
  }
}
</style>
